import {
  ChakraProvider,
  Container,
  Box
} from "@chakra-ui/react"
import { Header, TermsModal, Footer } from "./components";
import { theme } from "./theme";
import { Navigate, Route, Routes, BrowserRouter } from "react-router-dom";
import { useContext, useMemo } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { ToastContainer } from 'react-toastify';
import { AppConfigContext } from "./context";
import { appConfig } from "./config";
import { WalletConnectWalletAdapter } from '@solana/wallet-adapter-walletconnect';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import mixpanel from "mixpanel-browser";
import "./main.css";
import '@solana/wallet-adapter-react-ui/styles.css';
import 'react-toastify/dist/ReactToastify.css';
import WalletTracker from "./components/WalletTracker/WalletTracker";
import { DashboardPage } from "./pages/DashboardPage";
import { KyrosPage } from "./pages/KyrosPage";
import { AirdropPage } from "./pages/AirdropPage";
import { GovernancePage } from "./pages/GovernancePage";
import { StakePage } from "./pages/StakePage";
import { StakePageRoute } from "./pages/StakePageRoute";
import { StakeRedirect } from "./pages/StakeRedirect";
import { DripPage } from "./pages/DripPage";
import { ANSEM_PARTNER, ZCASH_PARTNER } from "./constants/partners";

export function App() {
  const { rpcUrl, mixpanelToken, mixpanelDebug } = useContext(AppConfigContext);

  const endpoint = useMemo(() => rpcUrl, [rpcUrl]);
  const wallets = useMemo(
    () => [
      new WalletConnectWalletAdapter({
        network: WalletAdapterNetwork.Mainnet,
        options: {
          relayUrl: 'wss://relay.walletconnect.com',
          projectId: '6ec9a8458680821c1140d97c60871aef',
          metadata: {
            name: 'Kyros',
            description: 'Kyros',
            url: 'https://app.kyros.fi',
            icons: ['https://statics.solscan.io/cdn/imgs/s60?ref=68747470733a2f2f6261667962656966336a7a726376776b747535626937776375787a7233366679676f34767163746174617079377a717861776f6a78726e657877692e697066732e647765622e6c696e6b2f'],
          },
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rpcUrl]
  );

  mixpanel.init(mixpanelToken, {
    debug: mixpanelDebug,
    track_pageview: true,
    persistence: "localStorage",
    ignore_dnt: true
  });

  return (
    <BrowserRouter>
      <ChakraProvider theme={theme}>
        <ConnectionProvider endpoint={endpoint}>
          <WalletProvider wallets={wallets} autoConnect>
            <WalletModalProvider>
              <AppConfigContext.Provider value={appConfig}>
                <WalletTracker />
                <Header />
                <ToastContainer />
                <Container
                  display={"flex"}
                  flexDirection={"column"}
                  maxW={"container.xl"}
                  paddingX={{ base: "1.8rem", sm: "2.8rem", md: "3.8rem", lg: "2rem", xl: "4.8rem" }}
                >
                  <TermsModal />
                  <Box as={"main"} flex={1}>
                    <Routes>
                      <Route path="/" element={<StakePage />} />
                      <Route path="/stake/:asset" element={<StakePageRoute />} />

                      {/* Legacy redirects */}
                      <Route path="/sol" element={<Navigate to="/stake/sol?mode=restake" replace />} />
                      <Route path="/jto" element={<Navigate to="/stake/jto?mode=restake" replace />} />
                      <Route path="/restake/:asset" element={<StakeRedirect mode="restake" />} />
                      <Route path="/redeem/:asset" element={<StakeRedirect mode="unstake" />} />
                      <Route path="/defi" element={<Navigate to="/stake/sol" replace />} />

                      {/* Kyros */}
                      <Route path="/kyros" element={<KyrosPage />} />

                      {/* Kyros Proposals */}
                      <Route
                        path="/governance/kyros-1" element={
                          <GovernancePage
                            title="KYROS-1: Burn 4.38M unclaimed KYROS airdrop allocation"
                            contentPath="/governance/kyros-1.md"
                            futarchyLink="https://v1.metadao.fi/kyros/trade-v5/GH8DFQjiSd9VwCZxzb3kzU2Jpx5JFC9gn8JNGKHfjrYa"
                            endTime={new Date('2026-01-16T13:30:00+01:00')}
                            isPassed={true}
                          />
                        }
                      />

                      {/* Drip */}
                      <Route path="/drip" element={<DripPage />} />

                      {/* Partner Drip pages */}
                      <Route path="/ansem" element={<DripPage partner={ANSEM_PARTNER} />} />
                      <Route path="/zcash" element={<DripPage partner={ZCASH_PARTNER} />} />

                      {/* Admin */}
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/chads" element={<AirdropPage />} />

                      {/* Else */}
                      <Route path={"*"} element={<Navigate to="/" replace />} />
                    </Routes>
                  </Box>
                </Container>
                <Footer />
              </AppConfigContext.Provider>
            </WalletModalProvider>
          </WalletProvider>
        </ConnectionProvider>
      </ChakraProvider>
    </BrowserRouter>
  )
}
